import * as yup from 'yup'
import pick from 'lodash/pick'
import type { TranslateFunction } from '../types/i18n'
import { composeRegex } from './misc'

import { DELIVERY_TYPE } from './api-constants'

// Phone validation regex
const phoneRegex = /^\+?[1-9]\d{1,14}$/

export function createLoginFormValidation(t: TranslateFunction) {
  return yup.object({
    email: yup.string().label(t('loginPage.form.email')).email().required(),
    password: yup
      .string()
      .label(t('loginPage.form.password'))
      .min(6)
      .required(),
  })
}

export function createRegistrationFormFields(t: TranslateFunction) {
  return {
    firstName: yup
      .string()
      .label(t('registrationPage.form.firstName'))
      .required(),
    lastName: yup
      .string()
      .label(t('registrationPage.form.lastName'))
      .required(),
    phone: yup
      .string()
      .label(t('registrationPage.form.phone'))
      .matches(phoneRegex, t('validation.invalidPhoneNumber'))
      .required(),
    email: yup
      .string()
      .label(t('registrationPage.form.email'))
      .email()
      .required(),
    password: yup
      .string()
      .label(t('registrationPage.form.password'))
      .min(6)
      .required(),
    passwordConfirmation: yup
      .string()
      .label(t('registrationPage.form.passwordConfirmation'))
      .oneOf(
        [yup.ref('password'), null],
        t('validation.passwordsDoesNotMatch'),
      )
      .required(),
  }
}

export function createMobileRegistrationFormFirstPart(t: TranslateFunction) {
  return yup.object({
    ...pick(createRegistrationFormFields(t), ['firstName', 'lastName', 'phone']),
  })
}

export function createMobileRegistrationFormSecondPart(t: TranslateFunction) {
  return yup.object({
    ...pick(createRegistrationFormFields(t), [
      'email',
      'password',
      'passwordConfirmation',
    ]),
  })
}

export function createRegistrationFormValidation(t: TranslateFunction) {
  return yup.object({
    ...createRegistrationFormFields(t),
  })
}

export function createUserSettingsFormValidation(t: TranslateFunction) {
  return yup.object({
    firstName: yup
      .string()
      .label(t('userSettingsForm.personalData.firstName'))
      .required(),
    lastName: yup
      .string()
      .label(t('userSettingsForm.personalData.lastName'))
      .required(),
    email: yup
      .string()
      .label(t('userSettingsForm.personalData.email'))
      .email()
      .required(),
    phone: yup
      .string()
      .label(t('userSettingsForm.personalData.phone'))
      .matches(phoneRegex, t('validation.invalidPhoneNumber'))
      .required(),
    password: yup
      .string()
      .label(t('userSettingsForm.password.newPassword'))
      .min(6),
    passwordConfirmation: yup
      .string()
      .label(t('userSettingsForm.password.newPasswordConfirmation'))
      .oneOf(
        [yup.ref('password'), null],
        t('validation.passwordsDoesNotMatch'),
      ),
    // TODO: Require passwordConfirmation to be present when password is
  })
}

export function createForgotPasswordFormValidation(t: TranslateFunction) {
  return yup.object({
    email: yup
      .string()
      .label(t('forgotPasswordPage.form.email'))
      .email()
      .required(),
  })
}

const trackNumberRejectRegex = composeRegex([
  // links
  /:\/\/.*/,

  // cyrillic letters
  /[\u0401\u0451\u0410-\u044f]/,

  // WAxxxxxxxxx
  /^WA.*/,

  // Wxxxxxxxxx (Apple.com order number)
  /^W.*/,

  // GS.xxxx-xxxx-xxxx (Google Store order number)
  /^GS.*/,

  // CARxxxxxxxx (Carter's order number)
  /^CAR.*/,

  // xx-xxxxx-xxxxx (eBay order number)
  /^\d{2}-.*/,

  // xxx-xxxxxxxx
  /^\d{3}-.*/,

  // whitespace char
  /.*\s.*/,
])

export function createTrackingNumber(t: TranslateFunction) {
  return yup
    .string()
    .required()
    .min(6)
    .trim()
    .test(
      'not-restricted-pattern',
      t('validation.invalidTrackingNumber'),
      (value) =>
        typeof value === 'string' && !value.match(trackNumberRejectRegex),
    )
    .label(t('addParcelForm.form.trackingNumber'))
}

export function createParcelDescriptionFormat(t: TranslateFunction) {
  return yup
    .string()
    .required()
    .trim()
    .test(
      'no-cyrillic-characters',
      t('validation.noCyrillicCharacters'),
      (value) =>
        typeof value === 'string' &&
        !value.match(/[а-яА-ЯЁё]/)
    )
    .label(t('addParcelForm.form.description'))
}

function createCommonParcelFormFields(t: TranslateFunction) {
  return {
    description: createParcelDescriptionFormat(t),
    parcelPrice: yup
      .number()
      .nullable()
      .min(0.001)
      .max(99999)
      .label(t('addParcelForm.form.price'))
      .required(),
  }
}

export function createAddParcelFormValidation(t: TranslateFunction) {
  const trackingNumber = createTrackingNumber(t)

  return [
    yup.object({
      trackingNumber,
      ...createCommonParcelFormFields(t),
    }),
    yup.object({
      address: yup
        .string()
        .nullable()
        .label(t('addParcelForm.form.address'))
        .when('deliveryType', {
          is: (deliveryType: number) => deliveryType === DELIVERY_TYPE.DELIVERY,
          // eslint-disable-next-line unicorn/no-thenable
          then: (schema) =>
            schema.label(t('addParcelForm.form.address')).required(),
        }),
      recipientId: yup
        .number()
        .label(t('addParcelForm.form.recipient'))
        .required(),
    }),
  ]
}

export function createEditParcelFormSchema(t: TranslateFunction) {
  return yup.object({
    ...createCommonParcelFormFields(t),
    address: yup.string().nullable().label(t('dashboardParcels.item.address')),
    recipientId: yup
      .number()
      .label(t('addParcelForm.form.recipient'))
      .required(),
  })
}

export function validateSearchForm(values: Record<string, any>) {
  let errors: Record<string, string> = {}

  if (!values.email) {
    errors.email = 'Required'
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = 'Invalid email address'
  }

  if (!values.text) {
    errors.text = 'Required'
  }

  return errors
}

export function fieldHasError(
  fieldName: string,
  formProps: any,
  forceErrorVisibility: boolean = false,
) {
  const hasError = formProps?.errors?.[fieldName]

  if (forceErrorVisibility) return hasError

  if (formProps?.submitCount === 0) return false

  return hasError
}

export function createRecipientFormValidation(t: TranslateFunction) {
  return yup.object({
    phone: yup
      .string()
      .nullable()
      .label(t('userSettingsForm.personalData.phone'))
      .matches(phoneRegex, t('validation.invalidPhoneNumber'))
      .required(),
    tin: yup.string().nullable().label(t('recipientForm.tin')).required(),
    passport_fullname: yup
      .string()
      .nullable()
      .label(t('recipientForm.passportSection.passportFullname'))
      .required(),
    passport_number: yup
      .string()
      .nullable()
      .label(t('recipientForm.passportSection.passportNumber'))
      .required(),
    passport_issued_by: yup
      .string()
      .nullable()
      .label(t('recipientForm.passportSection.passportIssuedBy'))
      .required(),
    passport_issued_at: yup
      .string()
      .nullable()
      .label(t('recipientForm.passportSection.passportIssuedAt'))
      .required(),
    passport_expires_at: yup
      .string()
      .nullable()
      .label(t('recipientForm.passportSection.passportExpiresAt'))
      .required(),
    city_id: yup
      .string()
      .nullable()
      .label(t('recipientForm.addressSection.city'))
      .required(),
    delivery_address: yup
      .string()
      .nullable()
      .label(t('recipientForm.addressSection.address'))
      .required(),
  })
}
